import type { BlogSnapshotRow } from "@shared/blog";

// Relative import on purpose: vitest.config.ts does not alias "@".
import { formatBlockValue } from "../../lib/blog/format";

// Static bar chart for DataBlock (D-10): plain SVG, no chart library, no hooks, no measuring and no
// animation, so the SSR markup and the hydrated markup are byte-identical. Geometry is in viewBox
// units (UI-SPEC checker amendments 3 and 4):
//   - 340 units wide so labels stay legible on a phone;
//   - label column 230..340, right-anchored: with direction="rtl", textAnchor="start" is the RIGHT
//     edge of the text (textAnchor="end" would push Arabic labels off the right of the viewBox);
//   - track 40..220, the filled bar grows from the track's right edge toward the left (RTL);
//   - 0..40 is the value gutter, so a 100% bar's value text stays inside the viewBox.
// Rows are drawn in the order given. Never sort here (D-04: the data block is never a ranking).

const VIEW_WIDTH = 340;
const ROW_HEIGHT = 40;
const BAR_HEIGHT = 24;
const LABEL_X = 336;
const TRACK_START = 40;
const TRACK_END = 220;
const TRACK_WIDTH = TRACK_END - TRACK_START;
const VALUE_GAP = 4;
const VALUE_MIN_X = 4;

const round2 = (n: number) => Math.round(n * 100) / 100;

const barWidth = (value: number, max: number): number => {
  const safe = Number.isFinite(value) && value > 0 ? value : 0;
  return round2(Math.min(TRACK_WIDTH, (safe / max) * TRACK_WIDTH));
};

export function BarChartSvg({
  rows,
  unit,
  title,
}: {
  rows: BlogSnapshotRow[];
  unit: "count" | "percent";
  title: string;
}) {
  const largest = rows.reduce(
    (acc, row) => (Number.isFinite(row.value) && row.value > acc ? row.value : acc),
    0,
  );
  const max = unit === "percent" ? 100 : largest > 0 ? largest : 1;
  const height = rows.length * ROW_HEIGHT;

  return (
    <svg
      viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
      width="100%"
      role="img"
      aria-label={title}
      className="block my-4"
    >
      {rows.map((row, index) => {
        const top = index * ROW_HEIGHT;
        const barY = top + (ROW_HEIGHT - BAR_HEIGHT) / 2;
        const textY = top + ROW_HEIGHT / 2;
        const width = barWidth(row.value, max);
        const barX = round2(TRACK_END - width);
        const valueX = Math.max(VALUE_MIN_X, round2(barX - VALUE_GAP));
        return (
          <g key={`${index}-${row.label}`}>
            <text
              x={LABEL_X}
              y={textY}
              direction="rtl"
              textAnchor="start"
              dominantBaseline="middle"
              fontSize={14}
              fill="#374151"
            >
              {row.label}
            </text>
            <rect
              x={TRACK_START}
              y={barY}
              width={TRACK_WIDTH}
              height={BAR_HEIGHT}
              rx={4}
              fill="#f3f4f6"
            />
            <rect x={barX} y={barY} width={width} height={BAR_HEIGHT} rx={4} fill="#f97316" />
            <text
              x={valueX}
              y={textY}
              direction="rtl"
              textAnchor="start"
              dominantBaseline="middle"
              fontSize={14}
              fontWeight={700}
              fill="#111827"
            >
              {formatBlockValue(row.value, unit)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export default BarChartSvg;
